/**
 * DeviceTypeProvider
 *
 * React context provider that exposes device classification
 * (desktop / tablet / mobile) to the entire component tree.
 *
 * Components consume via `useDeviceContext()` or the raw hook
 * `useDeviceType()` for standalone usage.
 *
 * Integrated with the Plugin UI Extensions layer so plugins
 * can access device info via `context.ui.isMobile / deviceType`.
 *
 * No domain logic — pure infrastructure.
 */

import React, { createContext, useContext } from 'react';
import { useDeviceType } from '../hooks/useDeviceType';
import type { DeviceInfo } from '../hooks/useDeviceType';
import type { DeviceType } from '../types/page-actions';

// ── Context ─────────────────────────────────────────────────────

const DeviceTypeContext = createContext<DeviceInfo>({
  deviceType: 'desktop',
  isMobile: false,
  isTouch: false,
  viewportWidth: 1200,
});

// ── Provider ────────────────────────────────────────────────────

export interface DeviceTypeProviderProps {
  children: React.ReactNode;
  /** Override breakpoints for testing or custom layouts */
  breakpoints?: {
    mobile?: number;
    tablet?: number;
  };
}

/**
 * Wraps the component tree with reactive device detection.
 *
 * Place at the top of the app tree (below PluginLoader, above routes)
 * so all pages and components can access device info without
 * re-running the resize listener.
 */
export function DeviceTypeProvider({ children, breakpoints }: DeviceTypeProviderProps) {
  const deviceInfo = useDeviceType(breakpoints);

  return (
    <DeviceTypeContext.Provider value={deviceInfo}>
      {children}
    </DeviceTypeContext.Provider>
  );
}

// ── Consumer hook ───────────────────────────────────────────────

/**
 * Access the device classification from the nearest DeviceTypeProvider.
 *
 * @example
 * ```tsx
 * const { isMobile, deviceType } = useDeviceContext();
 * ```
 */
export function useDeviceContext(): DeviceInfo {
  return useContext(DeviceTypeContext);
}

// ── Re-exports for convenience ──────────────────────────────────

export type { DeviceInfo, DeviceType };
